import React from 'react'
import NumberFlow from '@number-flow/react'
import usePageSettled from '@/hooks/usePageSettled'

// Format a value the same way NumberFlow would, but as a plain string.
// Used as the lightweight pre-settle render so we avoid spinning up the
// `<number-flow>` web component during the LCP window.
const formatPlain = (value, locales, format, prefix, suffix) => {
  const pre = prefix == null ? '' : prefix
  const suf = suffix == null ? '' : suffix
  if (value === null || value === undefined || value === '') return `${pre}${suf}`
  const num = typeof value === 'string' ? Number(value) : value
  if (Number.isNaN(num)) return `${pre}${value}${suf}`
  let formatted
  try {
    formatted = new Intl.NumberFormat(locales, format).format(num)
  } catch (_e) {
    formatted = String(num)
  }
  return `${pre}${formatted}${suf}`
}

// Drop-in replacement for `<NumberFlow />` that defers the real (animated)
// component until `usePageSettled()` flips to `true`. Pre-settle it renders
// a plain `<span>` with the value formatted the same way NumberFlow would,
// so layout/text content stays stable across the swap.
//
// Pass `deferUntilSettled={false}` to opt out (always animate immediately).
const DeferredNumberFlow = ({
  deferUntilSettled = true,
  fallback,
  value,
  locales,
  format,
  prefix,
  suffix,
  className,
  ...rest
}) => {
  const settled = usePageSettled()

  if (deferUntilSettled && !settled) {
    const text = fallback !== undefined ? fallback : formatPlain(value, locales, format, prefix, suffix)
    return <span className={className}>{text}</span>
  }

  return (
    <NumberFlow
      className={className}
      value={value}
      locales={locales}
      format={format}
      prefix={prefix}
      suffix={suffix}
      {...rest}
    />
  )
}

export default DeferredNumberFlow
